import { differenceWith, get, isArray, isEmpty, isEqual, isNil, snakeCase, sortBy, uniq } from "lodash";
import { stringify } from "PFCore/helpers/use_deterministic_stringity";
import { ApiRoute } from "PFCore/utilities/routes";

/*
 * API meta contains filters with this structure
 * This is known as metaFilters as it contains filter values AND filter config together
 *
 * MetaFilters
 * {
 *   meta: {
 *     filters: {
 *       availability: MetaFilter,
 *       strings: {
 *         key1: MetaFilter
 *       },
 *       fields: {
 *         key2: MetaFilter
 *       },
 *       numbers: {
 *         key3: MetaFilter
 *       }
 *     }
 *   }
 * }
 *
 * Each MetaFilter contains this structure
 * MetaFilter
 * {
 *   title: "Title1", //Title of the filter
 *   operator: ["any", "all", "lte", "gte"],
 *   facets: true / false , // indicates if the options come from HAL or not
 *   options: [], // When present this are the options to render
 *   ordinal: 200, // When present it is used to know the oder to render it
 *   value: null / [], // When present this filter contains a value from the user
 * }
 *
 * UI needs to preserve the same structure but with just the values
 * That is called ValueFilters
 *
 * ValueFilters
 * {
 *   valueFilters: {
 *     availability: {mode: "time_rules, ...} / null,
 *     strings: {
 *       key1: [12, 87]
 *     },
 *     fields: {
 *       key2: [98, 45]
 *     },
 *     numbers: {
 *       key3: {min, max, gte, lte}
 *     }
 *   }
 * }
 *
 * UI needs to strip the filters that have no values before sending it to API
 * That is called APIFilters
 *
 * */

/*
 * We need to flatten this array and order it in order to render the filters in many areas
 * filterElements returns the filters flatteded, ordered and excludes filters
 * (it is quite common to not render certain filters in filterbox)
 *
 * Returns MetaFilter[]
 * */

export const filterElements = (metaFilters, excludeList = [], excludeListChildren = []) => {
  if (isEmpty(metaFilters)) {
    return [];
  }

  const { availability, children, ...filters } = metaFilters;
  const getFilteredElements = (filters, isChildren) => {
    const filtersByKind = Object.values(filters);

    return filtersByKind
      .map((kindFilters) =>
        // @ts-ignore
        Object.keys(kindFilters).map((filterKind) => ({
          ...kindFilters[filterKind],
          name: filterKind,
          children: isChildren
        }))
      )
      .flat();
  };
  const filterElements = [
    ...getFilteredElements(filters),
    ...(children ? getFilteredElements(children, true) : [])
  ];

  const response = availability ? [...filterElements, availability] : [...filterElements];
  const filteredResponse = response
    .filter(({ hidden }) => !hidden)
    .filter(
      ({ name, children }) =>
        (!children && !(excludeList || []).includes(name)) ||
        (!!children && !(excludeListChildren || []).includes(name))
    );

  return sortBy(filteredResponse, ["ordinal", "name"]);
};

/*
 * We need to create an structure that mirrors the MetaFilters but contains just values
 *
 * metaFilters: MetaFilters TO valueFilters: ValueFilters
 *
 * This structure needs to preserve all keys even if the values are empty
 * Later when a filter changes value or gets a new one, we can use this structure to inject it
 *
 * returns ValueFilters
 * */
export const getValueFilters = (filters) => {
  if (isEmpty(filters)) {
    return filters;
  }

  const filterValues = JSON.parse(stringify(filters));

  Object.keys(filterValues).forEach((key) => {
    if (key === "availability") {
      filterValues[key] = filterValues[key].value || null;
      return;
    }
    if (key === "children") {
      filterValues.children = getValueFilters(filterValues.children);
    } else {
      const filters = filterValues[key];
      Object.keys(filters).forEach((filterName) => {
        const { value } = filters[filterName];
        filters[filterName] = value || null; //undefined won't work
      });
    }
  });

  return filterValues;
};

/*
 * We need to create the minimum structure that mirrors the values selected
 *
 * valueFilters: ValueFilters TO apiFilters: ValueFilters
 *
 * This structure needs to preserve all keys that have present values
 * It will clear nulls and empty arrays and empty objects
 *
 * returns ValueFilters
 * */
export const getAPIFilters = (valueFilters, excludeList = []) => {
  if (isEmpty(valueFilters)) {
    return valueFilters;
  }

  const valueFiltersCopy = JSON.parse(stringify(valueFilters));

  Object.keys(valueFiltersCopy).forEach((key) => {
    if (key === "availability") {
      if (isEmpty(valueFiltersCopy[key]) || excludeList.includes("availability")) {
        delete valueFiltersCopy[key];
      }
    } else {
      const innerFilters = valueFiltersCopy[key];
      Object.keys(innerFilters).forEach((filterName) => {
        const value = innerFilters[filterName];

        if (
          (isArray(value) && isEmpty(value)) ||
          value == null || // eslint-disable-line eqeqeq
          value === "" ||
          excludeList.includes(filterName)
        ) {
          if (filterName !== "parent_activity_id") {
            delete innerFilters[filterName];
          }
        }
      });

      if (isEmpty(valueFiltersCopy[key])) {
        delete valueFiltersCopy[key];
      }
    }
  });

  return valueFiltersCopy;
};

export const checkFiltersAreClear = (metaFilters, blockedList = []) => {
  const elements = filterElements(metaFilters, blockedList);
  return elements.every((item) => (isArray(item.value) ? isEmpty(item.value) : isNil(item.value)));
};

const getFiltersNestedKeys = (filters) => {
  const filtersNestedKeys = [];
  Object.keys(filters).forEach((familyKey) => {
    const familyNestedFiltersKeys = Object.keys(filters[familyKey]).map(
      (filterKey) => `${familyKey}.${filterKey}`
    );
    filtersNestedKeys.push(...familyNestedFiltersKeys);
  });

  return filtersNestedKeys;
};

const isFilterValueEqualDefaultValue = (filterValue, defaultValue) => {
  if (!filterValue && !defaultValue) {
    return true;
  }
  if (!filterValue || !defaultValue) {
    return false;
  }
  if (isArray(filterValue)) {
    // has same values regardless of order
    return (
      isEmpty(differenceWith(filterValue, defaultValue, isEqual)) &&
      isEmpty(differenceWith(defaultValue, filterValue, isEqual))
    );
  } else {
    return isEqual(filterValue, defaultValue);
  }
};

export const checkFiltersAreDefault = (valueFilters, defaultValueFilters) => {
  const { children: childrenValueFilters, ...rootValueFilters } = valueFilters;
  const { children: childrenDefaultFilters, ...rootDefaultFilters } = defaultValueFilters;

  const areFiltersDefault = (filters = {}, defaultFilters = {}) => {
    const filtersAPI = getAPIFilters(filters);
    const defaultFiltersAPI = getAPIFilters(defaultFilters);
    const filtersNestedKeys = uniq([
      ...getFiltersNestedKeys(filtersAPI),
      ...getFiltersNestedKeys(defaultFiltersAPI)
    ]);

    return filtersNestedKeys.every((filterNestedKey) => {
      const filterValueForKey = get(filtersAPI, filterNestedKey);
      const defaultFilterValueForKey = get(defaultFiltersAPI, filterNestedKey);

      return isFilterValueEqualDefaultValue(filterValueForKey, defaultFilterValueForKey);
    });
  };

  return (
    areFiltersDefault(rootValueFilters, rootDefaultFilters) &&
    areFiltersDefault(childrenValueFilters, childrenDefaultFilters)
  );
};

export const letFiltersRestore = (valueFilters, defaultFilters) =>
  !isEmpty(defaultFilters) && !checkFiltersAreDefault(valueFilters, defaultFilters);

export const letFilterRestore = (filter) =>
  !isEmpty(filter.defaultValue) && !isFilterValueEqualDefaultValue(filter.value, filter.defaultValue);

/**
 * returns a query function to for the filters of type "select_many", "select_one", or "checkboxes"
 *
 * @param {Object} filter - The meta filter object containing the filter config.
 * @param {Object} meta - The metadata object containing the filters to attach the query function to.
 * @param {boolean} children - The flag indicates if the options should relate to children or root filters.
 */
export const getFilterQuery = (filter, meta, children) => {
  if (filter.type === "select_many" || filter.type === "select_one" || filter.type === "checkboxes") {
    return ({ term, value }) =>
      filter.options
        ? Promise.resolve({
            entries: value ? filter.options.filter(({ id }) => value.includes(id)) : filter.options
          })
        : $.ajax({
            url: ApiRoute("/api/options/filters"),
            data: {
              search_id: meta.search_id || meta.searchId,
              name: snakeCase(filter.name),
              ...(isEmpty(term) ? null : { term }),
              ...(isEmpty(value) ? null : { value }),
              ...(!children ? null : { children: true })
            }
          });
  }
  return null;
};

export const getFilterDefault = (filter, meta) => {
  // eslint-disable-next-line no-unused-vars
  const { availability, ...otherFilters } = JSON.parse(JSON.stringify(meta.filters));

  if (isEmpty(meta.default_filters)) {
    return null;
  }

  if (filter.name === "availability") {
    return meta.default_filters.availability;
  }

  let defaultValue = null;
  Object.keys(otherFilters).forEach((key) => {
    const filters = otherFilters[key];
    Object.keys(filters).forEach((filterName) => {
      if (filterName === filter.name) {
        defaultValue = meta.default_filters[key][filterName];
      }
    });
  });

  return defaultValue;
};

export const handleRequestFiltersClear = (valueFilters) => {
  // eslint-disable-next-line no-unused-vars
  const { availability, ...otherFilters } = JSON.parse(JSON.stringify(valueFilters));

  Object.keys(otherFilters).forEach((key) => {
    const filters = otherFilters[key];
    Object.keys(filters).forEach((filterName) => {
      filters[filterName] = null;
    });
  });

  if (Object.keys(valueFilters).includes("availability")) {
    otherFilters.availability = {};
  }

  return otherFilters;
};

export const handleRequestFiltersChange = (valueFilters, filter, value) => {
  const { availability, ...otherFilters } = JSON.parse(JSON.stringify(valueFilters));

  if (filter.name === "availability") {
    return { ...otherFilters, availability: value };
  }

  // warning: filters will not be updated when current filters is {}
  // or does not include key("numbers", "strings", "fields")
  Object.keys(otherFilters).forEach((key) => {
    const filters = otherFilters[key];
    Object.keys(filters || {}).forEach((filterName) => {
      if (filterName === filter.name) {
        if (Array.isArray(value)) {
          value = value.map((item) => item?.id || item);
        }
        filters[filterName] = value?.id || value;
      }
    });
  });

  return { availability, ...otherFilters };
};

export const handleRequestFiltersReplace = (valueFilters, filters) => {
  const clearedFilters = handleRequestFiltersClear(valueFilters);
  const replacedValueFilters = { ...clearedFilters };

  Object.keys(filters).forEach((filtersFamily) => {
    replacedValueFilters[filtersFamily] = {
      ...replacedValueFilters[filtersFamily],
      ...filters[filtersFamily]
    };
  });

  return replacedValueFilters;
};

export const countFilters = (filters) => {
  const valueFilters = getValueFilters(filters);

  const { availability, fields, numbers, strings } = valueFilters;

  const countFilters = (filters) => Object.values(filters).filter((filterValue) => filterValue).length;

  const availabilityCount = availability ? 1 : 0;

  const fieldsCount = countFilters(fields);
  const stringsCount = countFilters(strings);
  const numbersCount = countFilters(numbers);

  return availabilityCount + fieldsCount + stringsCount + numbersCount;
};

// TODO: [ENG-84] Remove it or redo it
const formatFilters = (filters) => filters;
export default formatFilters;
